import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import { getCsrfToken } from "shared/form_helpers"
import { sendFailureNotification, sendSuccessNotification } from "shared/toast_notifications"
import { initializeTooltips } from "../../lib/shared/tooltip"
import { ChartAdapters } from "../../utils/chart_adapters"
import { DATA_SOURCES } from "constants/reports_constants"
import { regionStore } from "../../stores/locales/region_store"
import { buildingStore } from "../../stores/locales/building_store"
import { areaStore } from "../../stores/locales/area_store"
import { allProjectsStore } from "../../stores/all_projects_store"

const POLL_TIMEOUT_MS = 300000 // 5 minutes
const MAX_CONSECUTIVE_ERRORS = 3

/**
 * CustomReportBuilderController manages the interactive report builder UI.
 * Handles data source selection, column picking, filter building,
 * grouping/aggregation configuration, chart type selection, and report execution.
 *
 * Connects to data-controller="reports--custom-report-builder"
 */
export default class extends Controller {
    static targets = [
        "dataSourceSelect",
        "columnsContainer",
        "filtersContainer",
        "groupingSelect",
        "aggregationTypeSelect",
        "aggregationFieldContainer",
        "aggregationFieldSelect",
        "chartTypeSelect",
        "reportNameInput",
        "reportDescriptionInput",
        "resultsContainer",
        "resultsTable",
        "resultsBody",
        "resultsHead",
        "chartContainer",
        "chartCanvas",
        "loadingSpinner",
        "runButton",
        "saveButton",
        "clearButton",
        "savedReportsList",
        "savedReportsContainer",
        "noResultsMessage",
        "reportBuilderForm",
        "filterTemplate",
        "chartBuilderPanel",
        "keyboardHelpPanel",
        "enrichmentWarnings",
        "complianceConfigPanel",
        "complianceModeSelect",
        "complianceTargetStatusSelect",
        "complianceTestingTasksSelect",
        "complianceRegionSelect",
        "complianceBuildingSelect",
        "complianceAreaSelect",
        "complianceDeploymentStatusSelect",
        "complianceDeviceTypeSelect",
        "standardControlsSection",
        "generatedReportsTable",
        "generatedReportsBody",
        "myReportsCheckbox",
        "itemsLocationFilters",
        "itemsLocaleTemplate",
        "itemsLocaleMount",
        "itemsRegionSelect",
        "itemsBuildingSelect",
        "itemsAreaSelect",
        "complianceLocaleTemplate",
        "complianceLocaleMount",
        "projectFilterContainer",
        "projectFilterSelect"
    ]

    static values = {
        reportId: String,
        dataSources: Object,
        currentUserId: String
    }

    // We need these stores to tell us the currnetly selected region/building/area.
    // allProjectsStore feeds the Project Filter options; it is filled by the projects
    // supplier wrapping this builder (custom.html.erb), which pages /api/projects/list with
    // the page's organization-scope token so every org project is listed.
    static stores = [
        allProjectsStore,
        areaStore,
        buildingStore,
        regionStore
    ]

    connect() {
        this.availableProjects = []

        useStore(this)

        // allProjectsStore is a module-level singleton, so under Turbo Drive it can still hold
        // another page's project list (e.g. /projects/manage or the users page). Clear it before
        // rendering. This synchronous reset cannot wipe this page's data: the supplier only
        // writes the store after its awaited fetch resolves, which happens in a later task than
        // this connect() pass.
        this.setAllProjectsStoreValue([])

        this.dataSources = {}
        this.chartInstance = null
        this.currentResults = null
        this.currentReportExecutionId = null
        this._pollTimer = null
        this._handleKeydown = this._onKeydown.bind(this)
        document.addEventListener('keydown', this._handleKeydown)
        this.loadDataSources()
        this.loadSavedReports()
        this.loadGeneratedReports()
        initializeTooltips(this.element)

        // Auto-refresh generated reports table every 5 seconds
        this._reportsRefreshTimer = setInterval(() => {
            this.loadGeneratedReports()
        }, 5000)
    }

    disconnect() {
        document.removeEventListener('keydown', this._handleKeydown)
        if (this.chartInstance) {
            this.chartInstance.destroy()
            this.chartInstance = null
        }
        if (this._reportsRefreshTimer) {
            clearInterval(this._reportsRefreshTimer)
            this._reportsRefreshTimer = null
        }
    }

    // ─── Keyboard Shortcuts ─────────────────────────────────────────

    _onKeydown(event) {
        // Don't intercept when typing in inputs/textareas/selects
        const tag = event.target.tagName
        const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'

        // ? key toggles help (only when not in an input)
        if (event.key === '?' && !isInput) {
            event.preventDefault()
            this.toggleKeyboardHelp()
            return
        }

        // Escape closes help panel
        if (event.key === 'Escape') {
            if (this.hasKeyboardHelpPanelTarget && this.keyboardHelpPanelTarget.style.display !== 'none') {
                this.keyboardHelpPanelTarget.style.display = 'none'
                return
            }
        }

        // Ctrl/Cmd shortcuts
        const ctrlOrMeta = event.ctrlKey || event.metaKey
        if (!ctrlOrMeta) {
            return
        }

        if (event.key === 'Enter') {
            event.preventDefault()
            this.runReport(event)
        } else if (event.key === 's' && !event.shiftKey) {
            event.preventDefault()
            this.saveReport(event)
        } else if (event.key === 'X' && event.shiftKey) {
            event.preventDefault()
            this.clearForm(event)
        }
    }

    toggleKeyboardHelp() {
        if (!this.hasKeyboardHelpPanelTarget) {
            return
        }
        const panel = this.keyboardHelpPanelTarget
        const isHidden = panel.style.display === 'none'
        panel.style.display = isHidden ? 'block' : 'none'
    }

    // ─── Data Loading ───────────────────────────────────────────────

    loadDataSources() {
        DATA_SOURCES.forEach(source => {
            this.dataSources[source.id] = source
        })
    }

    async loadSavedReports() {
        try {
            const response = await fetch('/api/reports/report_definitions?report_type=custom', {
                headers: {
                    'Accept': 'application/json', 'X-CSRF-Token': getCsrfToken()
                }
            })
            if (!response.ok) {
                throw new Error('Failed to load saved reports')
            }
            const json = await response.json()
            this.renderSavedReportsList(json.data.reports || [])
        } catch (error) {
            console.error('Error loading saved reports:', error)
        }
    }

    // Project Filter options come from allProjectsStore (see static stores).
    onAllProjectsStoreUpdate(projects) {
        this.availableProjects = Array.isArray(projects) ? projects : []
        this.renderProjectFilterOptions(this.availableProjects)
    }

    renderProjectFilterOptions(projects) {
        if (!this.hasProjectFilterSelectTarget) {
            return
        }

        // Keep the current selection across re-renders (the store can update after a saved
        // report has already been loaded into the builder).
        const selectedIds = new Set(this.getSelectedProjects())
        const seenIds = new Set()

        this.projectFilterSelectTarget.innerHTML = ''
        projects.forEach(project => {
            if (!project || !project.id || seenIds.has(project.id)) {
                return
            }
            seenIds.add(project.id)

            const option = document.createElement('option')
            option.value = project.id
            option.textContent = project.name
            option.selected = selectedIds.has(project.id)
            this.projectFilterSelectTarget.appendChild(option)
        })
    }

    getSelectedProjects() {
        if (!this.hasProjectFilterSelectTarget) {
            return []
        }
        return Array.from(this.projectFilterSelectTarget.selectedOptions).map(opt => opt.value)
    }

    // ─── Generated Reports ──────────────────────────────────────────

    async loadGeneratedReports() {
        try {
            const response = await fetch('/api/reports?report_type=custom', {
                headers: {
                    'Accept': 'application/json', 'X-CSRF-Token': getCsrfToken()
                }
            })
            if (!response.ok) {
                throw new Error('Failed to load generated reports')
            }
            const json = await response.json()
            this._allGeneratedReports = json.data.reports || []
            this._renderFilteredReports()
        } catch (error) {
            console.error('Error loading generated reports:', error)
        }
    }

    onMyReportsFilterChange() {
        this._renderFilteredReports()
    }

    // ─── Items Location Filter Methods ──────────────────────────────
    showItemsLocationFilters() {
        if (this.hasItemsLocationFiltersTarget) {
            this.itemsLocationFiltersTarget.style.display = ''
        }
        this._mountLocaleGroup(this.itemsLocaleTemplateTarget, this.itemsLocaleMountTarget)
    }

    hideItemsLocationFilters() {
        this._unmountLocaleGroup(this.itemsLocaleMountTarget)
        // Clear any saved-template seed so the next mount starts blank unless a restore
        // re-seeds it.
        this._seedItemsLocaleTemplate({})
        if (this.hasItemsLocationFiltersTarget) {
            this.itemsLocationFiltersTarget.style.display = 'none'
        }
    }

    // ─── Locale Group Mount/Unmount Helpers ─────────────────────────

    _mountLocaleGroup(templateTarget, mountTarget) {
        if (!templateTarget || !mountTarget) {
            return
        }
        // Idempotent: if already mounted, do nothing so we don't tear down live widgets.
        if (mountTarget.childElementCount > 0) {
            return
        }
        mountTarget.appendChild(templateTarget.content.cloneNode(true))
    }

    _unmountLocaleGroup(mountTarget) {
        if (!mountTarget) {
            return
        }
        // Removing the widgets fires their disconnect(), which resets the shared stores.
        mountTarget.replaceChildren()
    }

    // Seed the items locale widgets' pre-selection (saved-template restore). Thin wrapper
    // over the shared seeding helper with the items group's template + element ids.
    _seedItemsLocaleTemplate(filters) {
        if (!this.hasItemsLocaleTemplateTarget) {
            return
        }
        this._seedLocaleTemplate(this.itemsLocaleTemplateTarget, {
            region: 'itemsRegionSelect',
            building: 'itemsBuildingSelect',
            area: 'itemsAreaSelect'
        }, filters)
    }

    // Seed the compliance locale widgets' pre-selection (saved-template restore). Thin
    // wrapper over the shared seeding helper with the compliance group's template +
    // element ids. Both groups reuse the same locale controllers
    // (selects--locales--region/building/area-select), so the seeded data-attribute names
    // are identical — only the template target and element ids differ.
    _seedComplianceLocaleTemplate(filters) {
        if (!this.hasComplianceLocaleTemplateTarget) {
            return
        }
        this._seedLocaleTemplate(this.complianceLocaleTemplateTarget, {
            region: 'complianceRegionSelect',
            building: 'complianceBuildingSelect',
            area: 'complianceAreaSelect'
        }, filters)
    }

    // Write the widgets' selected-value (and parent region/building) data attributes onto
    // an inert <template>'s region/building/area <select>s BEFORE the group is mounted, so
    // each locale controller reads them in connect() and cascades itself. We intentionally
    // set the pre-selection this way instead of poking `.value` and firing change handlers
    // (those imperative methods were removed in task 2.2). Passing empty/absent filters
    // writes empty strings, clearing any prior seed so the next mount starts blank
    // (Req 11.8, Property 5).
    _seedLocaleTemplate(templateTarget, ids, filters) {
        const content = templateTarget.content
        const region = content.getElementById(ids.region)
        const building = content.getElementById(ids.building)
        const area = content.getElementById(ids.area)

        const safeFilters = filters || {}
        const regionId = safeFilters.region || ''
        const buildingId = safeFilters.building || ''
        const areaId = safeFilters.area || ''

        if (region) {
            region.setAttribute('data-selects--locales--region-select-selected-value', regionId)
        }
        if (building) {
            building.setAttribute('data-selects--locales--building-select-selected-value', buildingId)
            building.setAttribute('data-selects--locales--building-select-parent-region-value', regionId)
        }
        if (area) {
            area.setAttribute('data-selects--locales--area-select-selected-value', areaId)
            area.setAttribute('data-selects--locales--area-select-parent-region-value', regionId)
            area.setAttribute('data-selects--locales--area-select-parent-building-value', buildingId)
        }
    }

    _renderFilteredReports() {
        let reports = this._allGeneratedReports || []
        if (this.hasMyReportsCheckboxTarget && this.myReportsCheckboxTarget.checked && this.currentUserIdValue) {
            reports = reports.filter(r => r.created_by?.id === this.currentUserIdValue)
        }
        // Sort by most recently created first
        reports = [...reports].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        this.renderGeneratedReportsTable(reports)
    }

    renderGeneratedReportsTable(reports) {
        if (!this.hasGeneratedReportsBodyTarget) {
            return
        }

        if (reports.length === 0) {
            this.generatedReportsBodyTarget.innerHTML = `
                <tr><td colspan="6" class="text-muted text-center">No generated reports yet.</td></tr>
            `
            return
        }

        this.generatedReportsBodyTarget.innerHTML = reports.map(report => {
            const createdAt = new Date(report.created_at).toLocaleString()
            const createdBy = report.created_by?.name || '—'
            const state = report.state || 'unknown'

            let stateHtml = ''
            let actionsHtml = ''

            if (state === 'completed') {
                stateHtml = '<span class="badge bg-success">completed</span>'
                actionsHtml = `
                    <div class="d-flex gap-1">
                        <a href="/api/reports/${ report.id }/download" class="btn btn-sm btn-outline-secondary"
                           target="_blank" download title="Download CSV">
                            <i class="bi bi-download"></i>
                        </a>
                        <button type="button" class="btn btn-sm btn-outline-primary"
                                data-action="click->reports--custom-report-builder#viewGeneratedReport"
                                data-report-id="${ report.id }"
                                title="View in Browser">
                            <i class="bi bi-table"></i>
                        </button>
                        <button type="button" class="btn btn-sm btn-outline-danger"
                                data-action="click->reports--custom-report-builder#deleteGeneratedReport"
                                data-report-id="${ report.id }"
                                title="Delete report">
                            <i class="bi bi-trash"></i>
                        </button>
                    </div>
                `
            } else if (state === 'running' || state === 'waiting') {
                stateHtml = `<span class="spinner-border spinner-border-sm me-1" role="status"></span><span class="text-muted">generating...</span>`
                actionsHtml = '<span class="text-muted">—</span>'
            } else if (state === 'failed') {
                stateHtml = '<span class="badge bg-danger">failed</span>'
                actionsHtml = `
                    <button type="button" class="btn btn-sm btn-outline-danger"
                            data-action="click->reports--custom-report-builder#deleteGeneratedReport"
                            data-report-id="${ report.id }"
                            title="Delete report">
                        <i class="bi bi-trash"></i>
                    </button>
                `
            } else {
                stateHtml = `<span class="badge bg-secondary">${ state }</span>`
                actionsHtml = '<span class="text-muted">—</span>'
            }

            return `
                <tr>
                    <td>${ report.name || '—' }</td>
                    <td>${ createdBy }</td>
                    <td>${ createdAt }</td>
                    <td>${ stateHtml }</td>
                    <td><small class="${ state === 'failed' ? 'text-danger' : 'text-muted' }">${ state === 'failed' && report.error_message ? report.error_message : (report.status || '—') }</small></td>
                    <td>${ actionsHtml }</td>
                </tr>
            `
        }).join('')
    }

    async viewGeneratedReport(event) {
        event.preventDefault()
        const reportId = event.currentTarget.dataset.reportId
        if (!reportId) {
            return
        }

        const btn = event.currentTarget
        btn.disabled = true
        btn.innerHTML = '<span class="spinner-border spinner-border-sm"></span>'

        try {
            const response = await fetch(`/api/reports/${ reportId }`, {
                headers: { 'Accept': 'application/json', 'X-CSRF-Token': getCsrfToken() }
            })
            if (!response.ok) {
                throw new Error('Failed to load report results')
            }
            const json = await response.json()
            const results = json?.data?.results || []
            const warnings = json?.data?.warnings || []
            const reportName = json?.data?.name || 'Report'
            const createdAt = json?.data?.created_at ? new Date(json.data.created_at).toLocaleString() : ''

            this.currentResults = { results, warnings, headers: json?.data?.headers }
            this.currentReportExecutionId = reportId

            // Show header above results
            const resultsRegion = document.getElementById('report-results-region')
            if (resultsRegion) {
                let headerEl = resultsRegion.querySelector('.report-view-header')
                if (!headerEl) {
                    headerEl = document.createElement('h6')
                    headerEl.className = 'report-view-header mt-3 mb-2'
                    resultsRegion.prepend(headerEl)
                }
                headerEl.textContent = `${ reportName } — created at ${ createdAt }`
            }

            await this.renderResults({ results, warnings, headers: json?.data?.headers })

            // Scroll to the header (with offset so it doesn't go behind navbar)
            if (resultsRegion) {
                const headerEl = resultsRegion.querySelector('.report-view-header')
                if (headerEl) {
                    headerEl.style.scrollMarginTop = '80px'
                    headerEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
            }
        } catch (error) {
            console.error('Error viewing report:', error)
            sendFailureNotification(error.message || 'Failed to load report results')
        } finally {
            btn.disabled = false
            btn.innerHTML = '<i class="bi bi-table"></i>'
        }
    }

    async deleteGeneratedReport(event) {
        event.preventDefault()
        const reportId = event.currentTarget.dataset.reportId
        if (!reportId) {
            return
        }
        if (!confirm('Are you sure you want to delete this report?')) {
            return
        }

        try {
            const response = await fetch(`/api/reports/${ reportId }`, {
                method: 'DELETE',
                headers: { 'Accept': 'application/json', 'X-CSRF-Token': getCsrfToken() }
            })
            if (!response.ok) {
                throw new Error('Failed to delete report')
            }
            sendSuccessNotification('Report deleted')
            this.loadGeneratedReports()
        } catch (error) {
            console.error('Error deleting report:', error)
            sendFailureNotification(error.message || 'Failed to delete report')
        }
    }

    // ─── Data Source Selection ───────────────────────────────────────

    onDataSourceChange(event) {
        const dataSource = event.target.value

        // Handle compliance data source
        if (dataSource === 'testing_task_compliance') {
            this.showCompliancePanel()
            this.hideItemsLocationFilters()
            this.clearResults()
            return
        }

        // For all other data sources, hide compliance and show standard
        this.hideCompliancePanel()

        if (!dataSource) {
            this.clearColumns()
            this.clearGroupings()
            this.clearFilters()
            this.hideItemsLocationFilters()
            this._toggleProjectFilter(false)
            return
        }

        const source = this.dataSources[dataSource]
        if (!source) {
            return
        }

        // Show project filter for items data source (Req 11.3)
        this._toggleProjectFilter(dataSource === 'items')

        this.renderColumns(source.columns)
        this.renderGroupingOptions(source.columns)
        this.renderAggregationFields(source.columns)
        this.clearFilters()
        this.clearResults()

        // Show location filter dropdowns for items data source
        if (dataSource === 'items') {
            this.showItemsLocationFilters()
        } else {
            this.hideItemsLocationFilters()
        }
    }

    // ─── Compliance Report Methods ──────────────────────────────────

    showCompliancePanel() {
        if (this.hasStandardControlsSectionTarget) {
            this.standardControlsSectionTarget.style.display = 'none'
        }
        if (this.hasComplianceConfigPanelTarget) {
            this.complianceConfigPanelTarget.style.display = ''
        }
        // Mount the compliance locale widgets so their controllers connect() and fetch
        // themselves (see _mountLocaleGroup). Only one location-filter group is mounted
        // at a time; the items group is unmounted by onDataSourceChange before this runs.
        if (this.hasComplianceLocaleTemplateTarget && this.hasComplianceLocaleMountTarget) {
            this._mountLocaleGroup(this.complianceLocaleTemplateTarget, this.complianceLocaleMountTarget)
        }
        this.fetchComplianceDropdowns()
    }

    hideCompliancePanel() {
        // Unmount the compliance locale widgets so their controllers disconnect() and the
        // disconnect-reset clears the shared locale stores.
        if (this.hasComplianceLocaleMountTarget) {
            this._unmountLocaleGroup(this.complianceLocaleMountTarget)
        }
        // Clear any saved-template seed so the next mount starts blank unless a restore
        // re-seeds it (mirrors hideItemsLocationFilters).
        this._seedComplianceLocaleTemplate({})
        if (this.hasComplianceConfigPanelTarget) {
            this.complianceConfigPanelTarget.style.display = 'none'
        }
        if (this.hasStandardControlsSectionTarget) {
            this.standardControlsSectionTarget.style.display = ''
        }
    }

    async fetchComplianceDropdowns() {
        try {
            // Fetch status names
            const statusResponse = await fetch('/api/testing_tasks/status_names', {
                headers: { 'Accept': 'application/json' }
            })
            if (statusResponse.ok) {
                const statusJson = await statusResponse.json()
                const statusNames = statusJson.data?.status_names || []
                this.populateTargetStatusDropdown(statusNames)
            }

            // Fetch testing tasks list
            const tasksResponse = await fetch('/api/testing_tasks/list', {
                headers: { 'Accept': 'application/json' }
            })
            if (tasksResponse.ok) {
                const tasksJson = await tasksResponse.json()
                this.populateTestingTasksSelect(tasksJson)
            }

            // Region/building/area are handled by the mounted store-backed locale widgets
            // (selects--locales--region/building/area-select), which fetch and cascade
            // themselves — the builder no longer seeds those three selects.

            // Fetch deployment statuses
            const statusesResponse = await fetch('/api/deployment_status/list', {
                headers: { 'Accept': 'application/json' }
            })
            if (statusesResponse.ok) {
                const statusesJson = await statusesResponse.json()
                const statuses = statusesJson.data?.deployment_statuses || statusesJson.deployment_statuses || []
                this.populateSelectDropdown(this.hasComplianceDeploymentStatusSelectTarget ? this.complianceDeploymentStatusSelectTarget : null, statuses, 'All Deployment Statuses')
            }

            // Fetch device types
            const deviceTypesResponse = await fetch('/api/device_types/device_type/list', {
                headers: { 'Accept': 'application/json' }
            })
            if (deviceTypesResponse.ok) {
                const deviceTypesJson = await deviceTypesResponse.json()
                const deviceTypes = deviceTypesJson.device_types || []
                this.populateSelectDropdown(this.hasComplianceDeviceTypeSelectTarget ? this.complianceDeviceTypeSelectTarget : null, deviceTypes, 'All Device Types')
            }
        } catch (error) {
            console.error('Error fetching compliance dropdowns:', error)
        }
    }

    populateSelectDropdown(target, items, placeholder) {
        if (!target) {
            return
        }
        target.innerHTML = `<option value="">${ placeholder }</option>`
        items.forEach(item => {
            const option = document.createElement('option')
            option.value = item.id
            option.textContent = item.name
            target.appendChild(option)
        })
    }

    populateTargetStatusDropdown(statusNames) {
        if (!this.hasComplianceTargetStatusSelectTarget) {
            return
        }
        const select = this.complianceTargetStatusSelectTarget
        select.innerHTML = '<option value="">Select a target status...</option>'
        statusNames.forEach(name => {
            const option = document.createElement('option')
            option.value = name
            option.textContent = name
            select.appendChild(option)
        })
    }

    populateTestingTasksSelect(tasksData) {
        if (!this.hasComplianceTestingTasksSelectTarget) {
            return
        }
        const container = this.complianceTestingTasksSelectTarget
        container.innerHTML = ''
        const tasks = tasksData.testing_tasks || []
        if (tasks.length === 0) {
            container.innerHTML = '<p class="text-muted mb-0 small">No testing tasks found.</p>'
            return
        }
        tasks.forEach(task => {
            const div = document.createElement('div')
            div.className = 'form-check'
            div.innerHTML = `
                <input class="form-check-input compliance-task-checkbox" type="checkbox"
                       value="${ task.id }" id="complianceTask_${ task.id }">
                <label class="form-check-label" for="complianceTask_${ task.id }">
                    ${ task.name }
                </label>
            `
            container.appendChild(div)
        })
    }

    getComplianceConfig() {
        const filters = {
            mode: this.hasComplianceModeSelectTarget ? this.complianceModeSelectTarget.value : 'summary',
            target_status: this.hasComplianceTargetStatusSelectTarget ? this.complianceTargetStatusSelectTarget.value : ''
        }

        // Testing task IDs (checkboxes)
        if (this.hasComplianceTestingTasksSelectTarget) {
            const checkedBoxes = this.complianceTestingTasksSelectTarget.querySelectorAll('.compliance-task-checkbox:checked')
            if (checkedBoxes.length > 0) {
                filters.testing_task_ids = Array.from(checkedBoxes).map(cb => cb.value)
            }
        }

        // Location filters — read from the authoritative locale stores (see getFilters).
        // The compliance group shares the same three singleton stores; only the active group
        // is mounted, so this is correct. Reading <select>.value here would miss cascade-
        // populated building/area selections whose <select>.value is empty at assembly time.
        const regionId = this.regionStoreValue?.id || ''
        const buildingId = this.buildingStoreValue?.id || ''
        const areaId = this.areaStoreValue?.id || ''
        if (regionId) {
            filters.region = regionId
        }
        if (buildingId) {
            filters.building = buildingId
        }
        if (areaId) {
            filters.area = areaId
        }
        if (this.hasComplianceDeploymentStatusSelectTarget && this.complianceDeploymentStatusSelectTarget.value) {
            filters.deployment_status = this.complianceDeploymentStatusSelectTarget.value
        }
        if (this.hasComplianceDeviceTypeSelectTarget && this.complianceDeviceTypeSelectTarget.value) {
            filters.device_type = this.complianceDeviceTypeSelectTarget.value
        }

        return filters
    }

    validateComplianceConfig() {
        if (!this.hasComplianceTargetStatusSelectTarget) {
            return false
        }
        if (!this.complianceTargetStatusSelectTarget.value) {
            sendFailureNotification('Please select a target status')
            return false
        }
        return true
    }

    // ─── Column Rendering ───────────────────────────────────────────

    renderColumns(columns) {
        if (!this.hasColumnsContainerTarget) {
            return
        }
        const container = this.columnsContainerTarget
        container.innerHTML = ''

        columns.forEach(col => {
            const div = document.createElement('div')
            div.className = 'card mb-2 column-config-card'
            div.dataset.columnName = col.name
            div.innerHTML = `
                <div class="card-body p-2">
                    <div class="d-flex align-items-center">
                        <input class="form-check-input column-checkbox me-2" type="checkbox"
                               id="col-${ col.name }" value="${ col.name }" checked
                               data-action="change->reports--custom-report-builder#onColumnToggle">
                        <label class="form-check-label flex-grow-1 fw-semibold" for="col-${ col.name }">
                            ${ col.description || col.name }
                        </label>
                        <button type="button" class="btn btn-sm btn-link text-muted p-0 column-config-toggle"
                                data-action="click->reports--custom-report-builder#toggleColumnConfig"
                                aria-label="Configure column ${ col.name }"
                                aria-expanded="false">
                            &#9881;
                        </button>
                    </div>
                    <div class="column-config-details mt-2" style="display: none;">
                        <div class="row g-2">
                            <div class="col-md-4">
                                <label class="form-label form-label-sm mb-0">Display Name</label>
                                <input type="text" class="form-control form-control-sm column-display-name"
                                       placeholder="${ col.description || col.name }"
                                       data-column="${ col.name }">
                            </div>
                            <div class="col-md-4">
                                <label class="form-label form-label-sm mb-0">Sort</label>
                                <select class="form-select form-select-sm column-sort-direction"
                                        data-column="${ col.name }">
                                    <option value="">None</option>
                                    <option value="asc">Ascending</option>
                                    <option value="desc">Descending</option>
                                </select>
                            </div>
                            <div class="col-md-4">
                                <label class="form-label form-label-sm mb-0">Format</label>
                                <select class="form-select form-select-sm column-format"
                                        data-column="${ col.name }">
                                    <option value="default">Default</option>
                                    ${ col.type === 'number' ? '<option value="currency">Currency</option><option value="decimal">Decimal</option>' : '' }
                                    ${ col.type === 'datetime' ? '<option value="date">Date Only</option><option value="datetime">Date & Time</option><option value="relative">Relative</option>' : '' }
                                    ${ col.type === 'string' ? '<option value="uppercase">UPPERCASE</option><option value="lowercase">lowercase</option>' : '' }
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            `
            container.appendChild(div)
        })
    }

    clearColumns() {
        if (this.hasColumnsContainerTarget) {
            this.columnsContainerTarget.innerHTML = '<p class="text-muted">Select a data source first</p>'
        }
    }

    onColumnToggle() {
        // Columns changed - clear results since they may be stale
    }

    toggleColumnConfig(event) {
        event.preventDefault()
        const card = event.target.closest('.column-config-card')
        if (!card) {
            return
        }
        const details = card.querySelector('.column-config-details')
        const toggle = card.querySelector('.column-config-toggle')
        if (details) {
            const isHidden = details.style.display === 'none'
            details.style.display = isHidden ? 'block' : 'none'
            if (toggle) {
                toggle.setAttribute('aria-expanded', isHidden ? 'true' : 'false')
            }
        }
    }

    getColumnConfigs() {
        if (!this.hasColumnsContainerTarget) {
            return {}
        }
        const configs = {}
        const cards = this.columnsContainerTarget.querySelectorAll('.column-config-card')

        cards.forEach(card => {
            const colName = card.dataset.columnName
            const checkbox = card.querySelector('.column-checkbox')
            if (!checkbox || !checkbox.checked) {
                return
            }

            const displayName = card.querySelector('.column-display-name')?.value || ''
            const sortDirection = card.querySelector('.column-sort-direction')?.value || ''
            const format = card.querySelector('.column-format')?.value || 'default'

            configs[colName] = {
                displayName: displayName || null, sortDirection: sortDirection || null, format: format
            }
        })

        return configs
    }

    getSelectedColumns() {
        if (!this.hasColumnsContainerTarget) {
            return []
        }
        const checkboxes = this.columnsContainerTarget.querySelectorAll('.column-checkbox:checked')
        return Array.from(checkboxes).map(cb => cb.value)
    }

    // ─── Grouping & Aggregation ─────────────────────────────────────

    renderGroupingOptions(columns) {
        if (!this.hasGroupingSelectTarget) {
            return
        }
        const select = this.groupingSelectTarget
        select.innerHTML = '<option value="">No grouping</option>'

        columns.filter(col => col.groupable).forEach(col => {
            const option = document.createElement('option')
            option.value = col.name
            option.textContent = col.description || col.name
            select.appendChild(option)
        })
    }

    clearGroupings() {
        if (this.hasGroupingSelectTarget) {
            this.groupingSelectTarget.innerHTML = '<option value="">No grouping</option>'
        }
    }

    renderAggregationFields(columns) {
        if (!this.hasAggregationFieldSelectTarget) {
            return
        }
        const select = this.aggregationFieldSelectTarget
        select.innerHTML = '<option value="">Select field</option>'

        columns.filter(col => col.aggregatable).forEach(col => {
            const option = document.createElement('option')
            option.value = col.name
            option.textContent = col.description || col.name
            select.appendChild(option)
        })
    }

    onAggregationTypeChange(event) {
        const aggType = event.target.value
        if (this.hasAggregationFieldContainerTarget) {
            // Show aggregation field selector for non-count types
            const needsField = aggType && aggType !== 'count'
            this.aggregationFieldContainerTarget.style.display = needsField ? 'block' : 'none'
        }
    }

    // ─── Filter Builder ─────────────────────────────────────────────

    addFilter(event) {
        if (event) {
            event.preventDefault()
        }
        const dataSource = this.hasDataSourceSelectTarget ? this.dataSourceSelectTarget.value : null
        if (!dataSource || !this.dataSources[dataSource]) {
            sendFailureNotification('Please select a data source first')
            return
        }

        const source = this.dataSources[dataSource]
        const filterIndex = this.filtersContainerTarget ? this.filtersContainerTarget.querySelectorAll('.filter-row').length : 0

        // For items data source, exclude location fields since they have dedicated dropdowns
        const locationFields = ['region', 'building', 'area', 'room']
        const filterableColumns = dataSource === 'items'
            ? source.columns.filter(col => !locationFields.includes(col.name))
            : source.columns

        const filterRow = document.createElement('div')
        filterRow.className = 'row mb-2 filter-row align-items-center'
        filterRow.dataset.filterIndex = filterIndex

        // Show AND/OR logic selector for second+ filters
        const logicHtml = filterIndex > 0 ? `<div class="col-md-1">
                   <select class="form-select form-select-sm filter-logic" aria-label="Filter logic">
                       <option value="and">AND</option>
                       <option value="or">OR</option>
                   </select>
               </div>` : '<div class="col-md-1"></div>'

        filterRow.innerHTML = `
            ${ logicHtml }
            <div class="col-md-3">
                <select class="form-select form-select-sm filter-field"
                        data-action="change->reports--custom-report-builder#onFilterFieldChange"
                        aria-label="Filter field">
                    <option value="">Select field</option>
                    ${ filterableColumns.map(col => `<option value="${ col.name }" data-type="${ col.type }">${ col.description || col.name }</option>`)
                                        .join('') }
                </select>
            </div>
            <div class="col-md-2">
                <select class="form-select form-select-sm filter-operator" aria-label="Filter operator">
                    <option value="equals">Equals</option>
                    <option value="not_equals">Not equals</option>
                    <option value="contains">Contains</option>
                    <option value="greater_than">Greater than</option>
                    <option value="less_than">Less than</option>
                </select>
            </div>
            <div class="col-md-5 filter-value-container">
                <input type="text" class="form-control form-control-sm filter-value"
                       placeholder="Filter value" aria-label="Filter value">
            </div>
            <div class="col-md-1">
                <button type="button" class="btn btn-sm btn-outline-danger"
                        data-action="click->reports--custom-report-builder#removeFilter"
                        aria-label="Remove filter">
                    &times;
                </button>
            </div>
        `

        if (this.hasFiltersContainerTarget) {
            this.filtersContainerTarget.appendChild(filterRow)
        }
    }

    onFilterFieldChange(event) {
        const filterRow = event.target.closest('.filter-row')
        if (!filterRow) {
            return
        }

        const selectedOption = event.target.selectedOptions[0]
        const fieldType = selectedOption?.dataset?.type || 'string'
        const operatorSelect = filterRow.querySelector('.filter-operator')
        const valueContainer = filterRow.querySelector('.filter-value-container')

        // Update operator options based on field type
        this.updateOperatorOptions(operatorSelect, fieldType)

        // Update value input based on field type
        this.updateFilterValueInput(valueContainer, fieldType)
    }

    updateOperatorOptions(operatorSelect, fieldType) {
        if (!operatorSelect) {
            return
        }

        let options = ''
        switch (fieldType) {
            case 'number':
                options = `
                    <option value="equals">Equals</option>
                    <option value="not_equals">Not equals</option>
                    <option value="greater_than">Greater than</option>
                    <option value="less_than">Less than</option>
                    <option value="greater_than_or_equal">Greater than or equal</option>
                    <option value="less_than_or_equal">Less than or equal</option>
                `
                break
            case 'datetime':
                options = `
                    <option value="equals">On date</option>
                    <option value="greater_than">After</option>
                    <option value="less_than">Before</option>
                    <option value="between">Between</option>
                `
                break
            default: // string
                options = `
                    <option value="equals">Equals</option>
                    <option value="not_equals">Not equals</option>
                    <option value="contains">Contains</option>
                    <option value="starts_with">Starts with</option>
                `
        }
        operatorSelect.innerHTML = options
    }

    updateFilterValueInput(valueContainer, fieldType) {
        if (!valueContainer) {
            return
        }

        switch (fieldType) {
            case 'number':
                valueContainer.innerHTML = `
                    <input type="number" class="form-control form-control-sm filter-value"
                           placeholder="Enter number" step="any" aria-label="Filter value">
                `
                break
            case 'datetime':
                valueContainer.innerHTML = `
                    <input type="date" class="form-control form-control-sm filter-value"
                           aria-label="Filter date value">
                `
                break
            default:
                valueContainer.innerHTML = `
                    <input type="text" class="form-control form-control-sm filter-value"
                           placeholder="Filter value" aria-label="Filter value">
                `
        }
    }

    removeFilter(event) {
        const filterRow = event.target.closest('.filter-row')
        if (filterRow) {
            filterRow.remove()
        }
    }

    clearFilters() {
        if (this.hasFiltersContainerTarget) {
            this.filtersContainerTarget.innerHTML = ''
        }
    }

    getFilters() {
        const filters = {}

        // Add location filters from items location dropdowns (IDs)
        const dataSource = this.hasDataSourceSelectTarget ? this.dataSourceSelectTarget.value : ''
        if (dataSource === 'items') {
            const regionId = this.regionStoreValue?.id || ''
            const buildingId = this.buildingStoreValue?.id || ''
            const areaId = this.areaStoreValue?.id || ''

            if (regionId) {
                filters.region = regionId
            }
            if (buildingId) {
                filters.building = buildingId
            }
            if (areaId) {
                filters.area = areaId
            }
        }

        // Add generic text-based filters
        if (!this.hasFiltersContainerTarget) {
            return filters
        }
        const rows = this.filtersContainerTarget.querySelectorAll('.filter-row')

        rows.forEach(row => {
            const field = row.querySelector('.filter-field')?.value
            const value = row.querySelector('.filter-value')?.value
            if (field && value) {
                if (!filters[field]) {
                    filters[field] = []
                }
                filters[field].push(value)
            }
        })

        return filters
    }

    validateFilters() {
        if (!this.hasFiltersContainerTarget) {
            return true
        }
        const rows = this.filtersContainerTarget.querySelectorAll('.filter-row')
        let valid = true

        rows.forEach(row => {
            const field = row.querySelector('.filter-field')
            const value = row.querySelector('.filter-value')

            // Clear previous validation state
            if (field) {
                field.classList.remove('is-invalid')
            }
            if (value) {
                value.classList.remove('is-invalid')
            }

            if (field?.value && !value?.value) {
                value?.classList.add('is-invalid')
                valid = false
            }
            if (!field?.value && value?.value) {
                field?.classList.add('is-invalid')
                valid = false
            }

            // Validate number fields
            if (field?.value && value?.value) {
                const selectedOption = field.selectedOptions[0]
                const fieldType = selectedOption?.dataset?.type
                if (fieldType === 'number' && isNaN(Number(value.value))) {
                    value.classList.add('is-invalid')
                    valid = false
                }
            }
        })

        return valid
    }

    // ─── Report Execution ───────────────────────────────────────────

    async runReport(event) {
        if (event) {
            event.preventDefault()
        }

        const config = this.buildReportConfig()
        if (!this.validateConfig(config)) {
            return
        }

        this.showLoading()
        this.clearResults()

        const headers = {
            'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': getCsrfToken()
        }

        try {
            // Always create an ephemeral definition for running with current form values.
            // This avoids issues with stale saved definitions or deleted reports.
            const ephemeralName = config.name || 'Ad-hoc Report'
            const uniqueName = `${ ephemeralName } (${ Date.now() })`
            const defResponse = await fetch('/api/reports/report_definitions', {
                method: 'POST',
                headers,
                body: JSON.stringify({ ...config, name: uniqueName, ttl: Math.floor(Date.now() / 1000) + 86400 })
            })

            if (!defResponse.ok) {
                const err = await defResponse.json()
                throw new Error(err.message || 'Failed to create report definition')
            }

            const defJson = await defResponse.json()
            const reportDefinitionId = defJson.data.id

            // Generate the report
            const generateParams = { report_definition_id: reportDefinitionId }

            // Include project filter if any projects are selected (Req 11.3, 11.4)
            const selectedProjects = this.getSelectedProjects()
            if (selectedProjects.length > 0) {
                generateParams.project = selectedProjects.length === 1 ? selectedProjects[0] : selectedProjects
            }

            const genResponse = await fetch('/api/reports/generate', {
                method: 'POST', headers, body: JSON.stringify(generateParams)
            })

            if (!genResponse.ok) {
                const err = await genResponse.json()
                throw new Error(err.message || 'Failed to generate report')
            }

            // Report submitted successfully - refresh the table and toast
            sendSuccessNotification('Report submitted - generating in background')
            this.loadGeneratedReports()
        } catch (error) {
            console.error('Error executing report:', error)
            sendFailureNotification(error.message || 'Failed to execute report')
        } finally {
            this.hideLoading()
        }
    }


    buildReportConfig() {
        const dataSource = this.hasDataSourceSelectTarget ? this.dataSourceSelectTarget.value : ''

        // For compliance reports, build config differently
        if (dataSource === 'testing_task_compliance') {
            return {
                name: this.hasReportNameInputTarget ? this.reportNameInputTarget.value : 'Ad-hoc Report',
                description: this.hasReportDescriptionInputTarget ? this.reportDescriptionInputTarget.value : '',
                data_source: 'testing_task_compliance',
                columns: [],
                filters: this.getComplianceConfig(),
                grouping_dimensions: [],
                aggregation_type: null,
                aggregation_field: null,
                chart_type: null
            }
        }

        const grouping = this.hasGroupingSelectTarget ? this.groupingSelectTarget.value : ''
        const aggType = this.hasAggregationTypeSelectTarget ? this.aggregationTypeSelectTarget.value : ''
        const aggField = this.hasAggregationFieldSelectTarget ? this.aggregationFieldSelectTarget.value : ''

        const config = {
            name: this.hasReportNameInputTarget ? this.reportNameInputTarget.value : 'Ad-hoc Report',
            description: this.hasReportDescriptionInputTarget ? this.reportDescriptionInputTarget.value : '',
            data_source: dataSource,
            columns: this.getSelectedColumns(),
            filters: this.getFilters(),
            grouping_dimensions: grouping ? [grouping] : [],
            aggregation_type: aggType || null,
            aggregation_field: aggField || null,
            chart_type: this.hasChartTypeSelectTarget ? this.chartTypeSelectTarget.value || null : null
        }

        // Include selected project filter in config for saved reports (Req 11.3)
        const selectedProjects = this.getSelectedProjects()
        if (selectedProjects.length > 0) {
            config.project_filter = selectedProjects
        }

        return config
    }

    validateConfig(config) {
        if (!config.data_source) {
            sendFailureNotification('Please select a data source')
            return false
        }

        // Compliance-specific validation
        if (config.data_source === 'testing_task_compliance') {
            return this.validateComplianceConfig()
        }

        // Standard validation
        if (!config.columns || config.columns.length === 0) {
            sendFailureNotification('Please select at least one column')
            return false
        }
        if (!this.validateFilters()) {
            sendFailureNotification('Please fix filter errors before running the report')
            return false
        }
        return true
    }

    // ─── Results Rendering ──────────────────────────────────────────

    async renderResults(data) {
        const results = data.results || []

        if (results.length === 0) {
            this.showNoResults()
            return
        }

        this.hideNoResults()
        // Display enrichment warnings if present
        this.displayEnrichmentWarnings(data.warnings || [])
        this.renderTable(results, data.headers || null)

        // Await renderChart so the Export button isn't enabled until the chart is
        // actually drawn (renderChart dynamically imports chart.js — fire-and-forget
        // would let a user click Export against an empty <canvas>).
        if (data.chart_data) {
            try {
                await this.renderChart(data.chart_data)
            } catch (error) {
                console.error('Failed to render chart:', error)
                sendFailureNotification('Failed to render chart')
            }
        } else {
            this.hideChart()
        }

        // Announce results count for screen readers
        const resultsRegion = document.getElementById('report-results-region')
        if (resultsRegion) {
            resultsRegion.setAttribute('aria-label', `Report results: ${ results.length } row${ results.length !== 1 ? 's' : '' } returned`)
        }

        // Dispatch report data to chart builder controller
        this.dispatchReportData(data)
    }

    renderTable(results, headers = null) {
        if (!this.hasResultsHeadTarget || !this.hasResultsBodyTarget) {
            return
        }

        this.tableData = results
        this.sortColumn = null
        this.sortDirection = 'asc'

        // Use provided headers for column order (compliance reports), or fall back to Object.keys
        const columns = headers && headers.length > 0
            ? headers
            : Object.keys(results[0])
        this._tableColumns = columns
        const configs = this.getColumnConfigs()

        // Get column type metadata from data source
        const dataSource = this.hasDataSourceSelectTarget ? this.dataSourceSelectTarget.value : null
        const sourceColumns = dataSource && this.dataSources[dataSource] ? this.dataSources[dataSource].columns : []

        // Build columnTypes map
        const columnTypes = {}
        columns.forEach(col => {
            const colMeta = sourceColumns.find(c => c.name === col)
            columnTypes[col] = colMeta?.type || 'string'
        })
        this._columnTypes = columnTypes

        // Build header with sortable click handlers
        this.resultsHeadTarget.innerHTML = `
            <tr>
                ${ columns.map(col => {
            const config = configs[col]
            const displayName = config?.displayName || this.formatColumnHeader(col)
            const typeClass = `col-${ columnTypes[col] }`
            return `<th scope="col" class="sortable-header ${ typeClass }" data-sort-col="${ col }" 
                                style="cursor: pointer; user-select: none;"
                                role="columnheader" aria-sort="none"
                                title="Click to sort by ${ displayName }">
                                ${ displayName } <span class="sort-indicator"></span>
                            </th>`
        }).join('') }
            </tr>
        `

        // Attach sort click handlers
        this.resultsHeadTarget.querySelectorAll('.sortable-header').forEach(th => {
            th.addEventListener('click', () => this.sortTable(th.dataset.sortCol))
        })

        this.renderTableBody(results, columns, configs, columnTypes)

        if (this.hasResultsContainerTarget) {
            this.resultsContainerTarget.style.display = 'block'
        }
    }

    sortTable(column) {
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
        } else {
            this.sortColumn = column
            this.sortDirection = 'asc'
        }

        const sorted = [...this.tableData].sort((a, b) => {
            let valA = a[column]
            let valB = b[column]

            // Handle nulls
            if (valA == null && valB == null) {
                return 0
            }
            if (valA == null) {
                return 1
            }
            if (valB == null) {
                return -1
            }

            // Numeric comparison
            if (typeof valA === 'number' && typeof valB === 'number') {
                return this.sortDirection === 'asc' ? valA - valB : valB - valA
            }

            // String comparison
            valA = String(valA).toLowerCase()
            valB = String(valB).toLowerCase()
            const cmp = valA.localeCompare(valB)
            return this.sortDirection === 'asc' ? cmp : -cmp
        })

        // Update sort indicators
        this.resultsHeadTarget.querySelectorAll('.sortable-header').forEach(th => {
            const indicator = th.querySelector('.sort-indicator')
            if (th.dataset.sortCol === column) {
                indicator.innerHTML = this.sortDirection === 'asc' ? ' <i class="bi bi-chevron-up"></i>' : ' <i class="bi bi-chevron-down"></i>'
                th.setAttribute('aria-sort', this.sortDirection === 'asc' ? 'ascending' : 'descending')
            } else {
                indicator.innerHTML = ''
                th.setAttribute('aria-sort', 'none')
            }
        })

        const columns = this._tableColumns || Object.keys(this.tableData[0])
        const configs = this.getColumnConfigs()

        // Rebuild columnTypes from stored value or recalculate
        const columnTypes = this._columnTypes || (() => {
            const dataSource = this.hasDataSourceSelectTarget ? this.dataSourceSelectTarget.value : null
            const sourceColumns = dataSource && this.dataSources[dataSource] ? this.dataSources[dataSource].columns : []
            const types = {}
            columns.forEach(col => {
                const colMeta = sourceColumns.find(c => c.name === col)
                types[col] = colMeta?.type || 'string'
            })
            return types
        })()

        this.renderTableBody(sorted, columns, configs, columnTypes)
    }

    renderTableBody(results, columns, configs, columnTypes = {}) {
        this.resultsBodyTarget.innerHTML = results.map(row => `
            <tr>
                ${ columns.map(col => {
            const config = configs[col]
            const value = row[col]
            const alignClass = columnTypes[col] === 'number' ? 'text-end' : ''
            return `<td class="${ alignClass }">${ this.formatCellValue(value, config?.format) }</td>`
        }).join('') }
            </tr>
        `).join('')
    }

    formatCellValue(value, format) {
        if (value == null) {
            return '<span class="text-muted">—</span>'
        }
        if (!format || format === 'default') {
            return value
        }

        switch (format) {
            case 'currency':
                return `$${ Number(value).toFixed(2) }`
            case 'decimal':
                return Number(value).toFixed(2)
            case 'date':
                return new Date(value * 1000).toLocaleDateString()
            case 'datetime':
                return new Date(value * 1000).toLocaleString()
            case 'relative': {
                const diff = Date.now() - new Date(value * 1000).getTime()
                const days = Math.floor(diff / 86400000)
                if (days === 0) {
                    return 'Today'
                }
                if (days === 1) {
                    return 'Yesterday'
                }
                return `${ days } days ago`
            }
            case 'uppercase':
                return String(value).toUpperCase()
            case 'lowercase':
                return String(value).toLowerCase()
            default:
                return value
        }
    }

    formatColumnHeader(key) {
        return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }

    async renderChart(chartData) {
        if (!this.hasChartCanvasTarget || !chartData) {
            return
        }

        // Destroy existing chart
        if (this.chartInstance) {
            this.chartInstance.destroy()
            this.chartInstance = null
        }

        const chartType = this.hasChartTypeSelectTarget ? this.chartTypeSelectTarget.value : 'bar'
        if (!chartType) {
            return
        }

        if (this.hasChartContainerTarget) {
            this.chartContainerTarget.style.display = 'block'
        }

        const colors = ChartAdapters.generateColorPalette(chartData.labels?.length || 0)
        const defaultOptions = ChartAdapters.getDefaultOptions(chartType)

        const { default: Chart } = await import('chart.js/auto')
        const ctx = this.chartCanvasTarget.getContext('2d')
        this.chartInstance = new Chart(ctx, {
            type: chartType, data: {
                labels: chartData.labels || [], datasets: [{
                    label: 'Report Data',
                    data: chartData.values || [],
                    backgroundColor: colors,
                    borderColor: colors,
                    borderWidth: 1
                }]
            }, options: defaultOptions
        })
    }

    hideChart() {
        if (this.hasChartContainerTarget) {
            this.chartContainerTarget.style.display = 'none'
        }
    }

    // ─── Save / Load / Delete ───────────────────────────────────────

    async saveReport(event) {
        if (event) {
            event.preventDefault()
        }

        const config = this.buildReportConfig()
        if (!config.name || config.name === 'Ad-hoc Report') {
            sendFailureNotification('Please enter a report name')
            return
        }
        if (!this.validateConfig(config)) {
            return
        }

        try {
            // Always create a new template. The Save button captures the current
            // form as a new report definition; it does not overwrite an existing one.
            const response = await fetch('/api/reports/report_definitions', {
                method: 'POST', headers: {
                    'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': getCsrfToken()
                }, body: JSON.stringify(config)
            })

            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.message || 'Failed to save report')
            }

            await response.json()
            // Do not retain the saved id. A subsequent Save should create
            // another new template rather than overwrite the one just created.
            this.reportIdValue = ''
            sendSuccessNotification('Template saved successfully')
            this.loadSavedReports()
        } catch (error) {
            console.error('Error saving report:', error)
            sendFailureNotification(error.message || 'Failed to save report')
        }
    }

    async loadReport(event) {
        event.preventDefault()
        const reportId = event.currentTarget.dataset.reportId
        if (!reportId) {
            return
        }

        try {
            const response = await fetch(`/api/reports/report_definitions/${ reportId }`, {
                headers: { 'Accept': 'application/json', 'X-CSRF-Token': getCsrfToken() }
            })
            if (!response.ok) {
                throw new Error('Failed to load report')
            }

            const json = await response.json()
            const report = json.data
            this.reportIdValue = report.id
            this.populateFormFromReport(report)
            sendSuccessNotification(`Loaded report: ${ report.name }`)
        } catch (error) {
            console.error('Error loading report:', error)
            sendFailureNotification('Failed to load report')
        }
    }

    async deleteReport(event) {
        event.preventDefault()
        event.stopPropagation()
        const reportId = event.currentTarget.dataset.reportId
        if (!reportId) {
            return
        }
        if (!confirm('Are you sure you want to delete this report?')) {
            return
        }

        try {
            const response = await fetch(`/api/reports/report_definitions/${ reportId }`, {
                method: 'DELETE', headers: { 'Accept': 'application/json', 'X-CSRF-Token': getCsrfToken() }
            })
            if (!response.ok) {
                throw new Error('Failed to delete report')
            }

            if (this.reportIdValue === reportId) {
                this.reportIdValue = ''
                this.clearForm()
            }
            sendSuccessNotification('Report deleted')
            this.loadSavedReports()
        } catch (error) {
            console.error('Error deleting report:', error)
            sendFailureNotification('Failed to delete report')
        }
    }

    // ─── Form Population ────────────────────────────────────────────

    populateFormFromReport(report) {
        // Set data source and trigger column rendering
        if (this.hasDataSourceSelectTarget) {
            this.dataSourceSelectTarget.value = report.data_source

            // Handle compliance reports
            if (report.data_source === 'testing_task_compliance') {
                // Restore saved location filters the same way the items group does: seed
                // the compliance locale widgets' selected-value (and parent) data
                // attributes on the template BEFORE mounting, so each locale controller
                // picks up the pre-selection in connect() and cascades itself. We must NOT
                // poke `.value` or fire change handlers — the imperative locale methods
                // were removed in task 2.2 (Req 11.8, Property 5).
                this._seedComplianceLocaleTemplate(report.filters || {})
                // Unmount the items group first so only one location-filter group is ever
                // mounted at a time (group isolation, Req 7); mirrors onDataSourceChange.
                this.hideItemsLocationFilters()
                this.showCompliancePanel()

                // Populate compliance config from saved filters
                const filters = report.filters || {}
                if (this.hasComplianceModeSelectTarget && filters.mode) {
                    this.complianceModeSelectTarget.value = filters.mode
                }

                // Wait for fetchComplianceDropdowns() to complete, then restore all filters
                setTimeout(() => {
                    // Restore target status
                    if (this.hasComplianceTargetStatusSelectTarget && filters.target_status) {
                        this.complianceTargetStatusSelectTarget.value = filters.target_status
                    }

                    // Restore testing task checkboxes
                    if (filters.testing_task_ids && this.hasComplianceTestingTasksSelectTarget) {
                        const taskIds = Array.isArray(filters.testing_task_ids) ? filters.testing_task_ids : [filters.testing_task_ids]
                        const checkboxes = this.complianceTestingTasksSelectTarget.querySelectorAll('.compliance-task-checkbox')
                        checkboxes.forEach(cb => {
                            cb.checked = taskIds.includes(cb.value)
                        })
                    }

                    // Restore location filters
                    if (this.hasComplianceRegionSelectTarget && filters.region) {
                        this.complianceRegionSelectTarget.value = filters.region
                    }
                    if (this.hasComplianceBuildingSelectTarget && filters.building) {
                        this.complianceBuildingSelectTarget.value = filters.building
                    }
                    if (this.hasComplianceAreaSelectTarget && filters.area) {
                        this.complianceAreaSelectTarget.value = filters.area
                    }

                    // Restore deployment status and device type
                    if (this.hasComplianceDeploymentStatusSelectTarget && filters.deployment_status) {
                        this.complianceDeploymentStatusSelectTarget.value = filters.deployment_status
                    }
                    if (this.hasComplianceDeviceTypeSelectTarget && filters.device_type) {
                        this.complianceDeviceTypeSelectTarget.value = filters.device_type
                    }
                }, 500)
            } else {
                this.hideCompliancePanel()
                const source = this.dataSources[report.data_source]
                if (source) {
                    this.renderColumns(source.columns)
                    this.renderGroupingOptions(source.columns)
                    this.renderAggregationFields(source.columns)
                }

                // Show location filters for items and populate with saved values
                if (report.data_source === 'items') {
                    this._seedItemsLocaleTemplate(report.filters || {})
                    this.showItemsLocationFilters()
                } else {
                    this.hideItemsLocationFilters()
                }
            }
        }

        // Set report name and description
        if (this.hasReportNameInputTarget) {
            this.reportNameInputTarget.value = report.name || ''
        }
        if (this.hasReportDescriptionInputTarget) {
            this.reportDescriptionInputTarget.value = report.description || ''
        }

        // Check only the saved columns
        if (report.columns && this.hasColumnsContainerTarget) {
            const checkboxes = this.columnsContainerTarget.querySelectorAll('.column-checkbox')
            checkboxes.forEach(cb => {
                cb.checked = report.columns.includes(cb.value)
            })
        }

        // Set grouping
        if (this.hasGroupingSelectTarget && report.grouping_dimensions) {
            this.groupingSelectTarget.value = report.grouping_dimensions[0] || ''
        }

        // Set aggregation
        if (this.hasAggregationTypeSelectTarget) {
            this.aggregationTypeSelectTarget.value = report.aggregation_type || ''
            // Trigger visibility of aggregation field
            if (this.hasAggregationFieldContainerTarget) {
                const needsField = report.aggregation_type && report.aggregation_type !== 'count'
                this.aggregationFieldContainerTarget.style.display = needsField ? 'block' : 'none'
            }
        }
        if (this.hasAggregationFieldSelectTarget) {
            this.aggregationFieldSelectTarget.value = report.aggregation_field || ''
        }

        // Set chart type
        if (this.hasChartTypeSelectTarget) {
            this.chartTypeSelectTarget.value = report.chart_type || ''
        }

        // Rebuild filters
        this.clearFilters()
        if (report.filters && typeof report.filters === 'object') {
            Object.entries(report.filters).forEach(([field, values]) => {
                const valuesArray = Array.isArray(values) ? values : [values]
                valuesArray.forEach(value => {
                    this.addFilter()
                    const rows = this.filtersContainerTarget.querySelectorAll('.filter-row')
                    const lastRow = rows[rows.length - 1]
                    if (lastRow) {
                        const fieldSelect = lastRow.querySelector('.filter-field')
                        const valueInput = lastRow.querySelector('.filter-value')
                        if (fieldSelect) {
                            fieldSelect.value = field
                        }
                        if (valueInput) {
                            valueInput.value = value
                        }
                    }
                })
            })
        }

        // Restore project filter selection
        if (report.data_source === 'items') {
            this._toggleProjectFilter(true)
            if (report.project_filter && this.hasProjectFilterSelectTarget) {
                const projectIds = Array.isArray(report.project_filter) ? report.project_filter : [report.project_filter]
                Array.from(this.projectFilterSelectTarget.options).forEach(opt => {
                    opt.selected = projectIds.includes(opt.value)
                })
            }
        } else {
            this._toggleProjectFilter(false)
        }
    }

    // ─── Saved Reports List ─────────────────────────────────────────

    renderSavedReportsList(reports) {
        if (!this.hasSavedReportsListTarget) {
            return
        }
        const list = this.savedReportsListTarget

        if (reports.length === 0) {
            list.innerHTML = '<li class="list-group-item text-muted" role="option" aria-selected="false">No report templates</li>'
            return
        }

        list.innerHTML = reports.map(report => {
            const tagBadges = (report.tags || []).map(tag => `<span class="badge bg-light text-dark ms-1">${ tag }</span>`)
                                                 .join('')

            return `
            <li class="list-group-item d-flex justify-content-between align-items-center saved-report-item"
                role="option" aria-selected="false" tabindex="0"
                data-action="keydown->reports--custom-report-builder#onSavedReportKeydown"
                data-report-id="${ report.id }">
                <a href="#" class="text-decoration-none flex-grow-1"
                   data-action="click->reports--custom-report-builder#loadReport"
                   data-report-id="${ report.id }">
                    ${ report.name }
                    <small class="text-muted d-block">${ report.data_source }${ tagBadges }</small>
                </a>
                <div class="btn-group btn-group-sm ms-2">
                    <button type="button" class="btn btn-outline-danger"
                            data-action="click->reports--custom-report-builder#deleteReport"
                            data-report-id="${ report.id }"
                            aria-label="Delete report ${ report.name }">
                        &times;
                    </button>
                </div>
            </li>`
        }).join('')
    }

    // ─── Saved Report Keyboard Navigation ───────────────────────────

    onSavedReportKeydown(event) {
        const item = event.currentTarget
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            const link = item.querySelector('a[data-report-id]')
            if (link) {
                link.click()
            }
        } else if (event.key === 'ArrowDown') {
            event.preventDefault()
            const next = item.nextElementSibling
            if (next) {
                next.focus()
            }
        } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            const prev = item.previousElementSibling
            if (prev) {
                prev.focus()
            }
        }
    }

    // ─── Chart Builder Integration ──────────────────────────────────

    dispatchReportData(data) {
        window.dispatchEvent(new CustomEvent('report-data-updated', {
            detail: {
                reportData: data.results || [],
                availableFields: this.getAvailableFieldsMetadata(),
                chartData: data.chart_data || null
            }
        }))
    }

    getAvailableFieldsMetadata() {
        const dataSource = this.hasDataSourceSelectTarget ? this.dataSourceSelectTarget.value : null
        if (!dataSource || !this.dataSources[dataSource]) {
            return []
        }

        const source = this.dataSources[dataSource]
        return (source.columns || []).map(col => ({
            name: col.name,
            display_name: col.description || col.name,
            type: col.type || 'string',
            groupable: col.groupable || false,
            aggregatable: col.aggregatable || false
        }))
    }

    onChartTypeChanged(event) {
        const chartType = event.target.value
        const panel = document.getElementById('chartBuilderPanel')
        if (panel) {
            panel.style.display = chartType ? 'block' : 'none'
        }
    }

    toggleChartBuilder(event) {
        if (event) {
            event.preventDefault()
        }
        const body = document.getElementById('chartBuilderBody')
        const icon = this.element.querySelector('.chart-builder-toggle-icon')
        if (body) {
            const isHidden = body.style.display === 'none'
            body.style.display = isHidden ? 'block' : 'none'
            if (icon) {
                icon.innerHTML = isHidden ? '&#9660;' : '&#9654;'
            }
            const btn = event?.currentTarget
            if (btn) {
                btn.setAttribute('aria-expanded', isHidden ? 'true' : 'false')
            }
        }
    }

    // ─── UI Helpers ─────────────────────────────────────────────────

    clearForm(event) {
        if (event) {
            event.preventDefault()
        }
        this.reportIdValue = ''

        if (this.hasReportNameInputTarget) {
            this.reportNameInputTarget.value = ''
        }
        if (this.hasReportDescriptionInputTarget) {
            this.reportDescriptionInputTarget.value = ''
        }
        if (this.hasDataSourceSelectTarget) {
            this.dataSourceSelectTarget.value = ''
        }
        if (this.hasChartTypeSelectTarget) {
            this.chartTypeSelectTarget.value = ''
        }
        if (this.hasAggregationTypeSelectTarget) {
            this.aggregationTypeSelectTarget.value = ''
        }
        if (this.hasAggregationFieldSelectTarget) {
            this.aggregationFieldSelectTarget.value = ''
        }
        if (this.hasGroupingSelectTarget) {
            this.groupingSelectTarget.value = ''
        }

        this.clearColumns()
        this.clearGroupings()
        this.clearFilters()
        this.clearResults()

        // Reset project filter
        this._toggleProjectFilter(false)

        if (this.hasAggregationFieldContainerTarget) {
            this.aggregationFieldContainerTarget.style.display = 'none'
        }

        // Hide chart builder panel
        const panel = document.getElementById('chartBuilderPanel')
        if (panel) {
            panel.style.display = 'none'
        }

        // Reset compliance panel
        if (this.hasComplianceConfigPanelTarget) {
            this.complianceConfigPanelTarget.style.display = 'none'
        }
        if (this.hasStandardControlsSectionTarget) {
            this.standardControlsSectionTarget.style.display = ''
        }
        if (this.hasComplianceModeSelectTarget) {
            this.complianceModeSelectTarget.value = 'summary'
        }
        if (this.hasComplianceTargetStatusSelectTarget) {
            this.complianceTargetStatusSelectTarget.value = ''
        }
        if (this.hasComplianceTestingTasksSelectTarget) {
            this.complianceTestingTasksSelectTarget.querySelectorAll('.compliance-task-checkbox').forEach(cb => {
                cb.checked = false
            })
        }
    }

    clearResults() {
        this.currentResults = null
        this.currentReportExecutionId = null
        if (this.hasResultsContainerTarget) {
            this.resultsContainerTarget.style.display = 'none'
        }
        if (this.hasResultsHeadTarget) {
            this.resultsHeadTarget.innerHTML = ''
        }
        if (this.hasResultsBodyTarget) {
            this.resultsBodyTarget.innerHTML = ''
        }
        if (this.hasEnrichmentWarningsTarget) {
            this.enrichmentWarningsTarget.style.display = 'none'
            this.enrichmentWarningsTarget.innerHTML = ''
        }
        this.hideChart()
        this.hideNoResults()
    }

    /**
     * Resolve the shared argus--table controller on the Reports table
     * (#table-holder). That table renders with store updates disabled, so it
     * cannot observe the report run itself — the report builder drives its
     * loading overlay directly. Returns null if the table or Stimulus app is
     * not present on the page.
     */
    _getArgusTable() {
        const el = document.getElementById('table-holder')
        if (!el || !window.Stimulus) {
            return null
        }
        return window.Stimulus.getControllerForElementAndIdentifier(el, 'argus--table')
    }

    showLoading() {
        this._getArgusTable()?.showLoading()
        if (this.hasRunButtonTarget) {
            this.runButtonTarget.disabled = true
        }
    }

    hideLoading() {
        this._getArgusTable()?.hideLoading()
        if (this.hasRunButtonTarget) {
            this.runButtonTarget.disabled = false
        }
    }

    showNoResults() {
        if (this.hasNoResultsMessageTarget) {
            const dataSource = this.hasDataSourceSelectTarget ? this.dataSourceSelectTarget.value : ''
            const filterCount = this.hasFiltersContainerTarget ?
                this.filtersContainerTarget.querySelectorAll('.filter-row').length : 0

            let message = 'No data found matching your criteria.'
            const suggestions = []

            if (filterCount > 0) {
                suggestions.push('Try broadening or removing some filters')
            }
            if (dataSource) {
                suggestions.push(`Verify that you have data in the "${ this.formatDataSourceName(dataSource) }" data source`)
            }
            suggestions.push('Check that you have access to the selected locations')

            if (suggestions.length > 0) {
                message += '<ul class="mb-0 mt-2">' + suggestions.map(s => `<li>${ s }</li>`).join('') + '</ul>'
            }

            this.noResultsMessageTarget.innerHTML = message
            this.noResultsMessageTarget.style.display = 'block'
        }
        if (this.hasResultsContainerTarget) {
            this.resultsContainerTarget.style.display = 'none'
        }
    }

    hideNoResults() {
        if (this.hasNoResultsMessageTarget) {
            this.noResultsMessageTarget.style.display = 'none'
        }
        if (this.hasEnrichmentWarningsTarget) {
            this.enrichmentWarningsTarget.style.display = 'none'
        }
    }

    displayEnrichmentWarnings(warnings) {
        if (!this.hasEnrichmentWarningsTarget) {
            return
        }

        if (!warnings || warnings.length === 0) {
            this.enrichmentWarningsTarget.style.display = 'none'
            this.enrichmentWarningsTarget.innerHTML = ''
            return
        }

        const warningMessages = warnings.map(w => {
            switch (w.source || w.type) {
                case 'deployment_status':
                    return 'Some deployment status names could not be resolved'
                case 'device_type':
                    return 'Some device type names could not be resolved'
                case 'locale':
                    return 'Some location names could not be resolved'
                default:
                    return w.message || 'Some reference data could not be resolved'
            }
        })

        this.enrichmentWarningsTarget.innerHTML = `
            <div class="alert alert-warning d-flex align-items-start" role="alert">
                <i class="bi bi-exclamation-triangle-fill me-2 mt-1" aria-hidden="true"></i>
                <div>
                    <strong>Partial data:</strong> ${ warningMessages.join('. ') }. Raw identifiers are shown where names could not be resolved.
                </div>
            </div>
        `
        this.enrichmentWarningsTarget.style.display = 'block'
    }

    _toggleProjectFilter(show) {
        if (this.hasProjectFilterContainerTarget) {
            this.projectFilterContainerTarget.style.display = show ? 'block' : 'none'
        }
        // Clear selection when hiding
        if (!show && this.hasProjectFilterSelectTarget) {
            this.projectFilterSelectTarget.selectedIndex = -1
        }
    }

    formatDataSourceName(dataSource) {
        const names = {
            'items': 'Items',
            'locales': 'Locales',
            'device_types': 'Device Types',
            'deployment_statuses': 'Deployment Statuses',
            'testing_task_compliance': 'Testing Task Compliance'
        }
        return names[dataSource] || dataSource
    }
}
